import {
  ApiServerDatabases,
  Cluster,
  ClusterDatabaseEntry,
  Configuration,
  Database,
  ResponseError as SdkResponseError,
  TypesDatabase,
  TypesDatabaseMetadata,
  V1Neo4jCluster,
  V2ClustersApi,
  V2DatabasesApi,
  V2ServersApi,
} from '@internal/backstage-plugin-neo4j-databases-common';
import {
  ApiRef,
  ConfigApi,
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/frontend-plugin-api';
import { BackendAuthApi } from '@internal/plugin-neo4j-auth-common';
import { ResponseError as BackstageResponseError } from '@backstage/errors';

import { DatabaseManagerApiError } from './errors';

const DATABASE_MANAGER_PUBLIC_URL = 'neo4jDatabases.databaseManagerPublicUrl';

export class DatabaseManagerRestApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApiBackstage: FetchApi;
  private readonly config: ConfigApi;
  private readonly backendAuthApi: BackendAuthApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    config: ConfigApi;
    backendAuthApi: BackendAuthApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApiBackstage = options.fetchApi;
    this.config = options.config;
    this.backendAuthApi = options.backendAuthApi;
  }

  private async getDatabasesApi(): Promise<V2DatabasesApi> {
    const databaseManagerPublicUrl = this.config.getString(
      DATABASE_MANAGER_PUBLIC_URL,
    );

    const apiConfig = new Configuration({
      basePath: databaseManagerPublicUrl,
      headers: {
        Authorization: `Bearer ${await this.backendAuthApi.getToken()}`,
      },
    });

    return new V2DatabasesApi(apiConfig);
  }

  private async getClustersApi(): Promise<V2ClustersApi> {
    const databaseManagerPublicUrl = this.config.getString(
      DATABASE_MANAGER_PUBLIC_URL,
    );

    const apiConfig = new Configuration({
      basePath: databaseManagerPublicUrl,
      headers: {
        Authorization: `Bearer ${await this.backendAuthApi.getToken()}`,
      },
    });

    return new V2ClustersApi(apiConfig);
  }

  private async getServersApi(): Promise<V2ServersApi> {
    const databaseManagerPublicUrl = this.config.getString(
      DATABASE_MANAGER_PUBLIC_URL,
    );

    const apiConfig = new Configuration({
      basePath: databaseManagerPublicUrl,
      headers: {
        Authorization: `Bearer ${await this.backendAuthApi.getToken()}`,
      },
    });

    return new V2ServersApi(apiConfig);
  }

  /**
   * Helper function to handle errors from SDK calls
   */
  private async handleSdkError(error: unknown): Promise<never> {
    if (error instanceof SdkResponseError) {
      const response = error.response;
      let errorMessage = error.message || response.statusText;
      let errorCode: number | undefined;
      let errors: string[] | undefined;

      try {
        const json = await response.json();
        if (json.message) {
          errorMessage = json.message;
        } else if (json.error) {
          errorMessage = json.error;
        } else if (typeof json === 'string') {
          errorMessage = json;
        }

        if (typeof json.code === 'number') {
          errorCode = json.code;
        }

        if (Array.isArray(json.errors)) {
          errors = json.errors.filter(
            (e: unknown): e is string => typeof e === 'string',
          );
        }
      } catch {
        // Failed to parse error response, use default message
      }

      throw new DatabaseManagerApiError(
        errorMessage,
        response.status,
        errorCode,
        errors,
      );
    }

    if (error instanceof Error) {
      throw new DatabaseManagerApiError(error.message, 400);
    }

    throw new DatabaseManagerApiError('Unknown error', 400);
  }

  private async fetch(
    apiPath: string,
    method: string,
    body?: object,
  ): Promise<Response> {
    const baseUrl = await this.discoveryApi.getBaseUrl('neo4j-databases');

    const contentType = body
      ? { 'Content-Type': 'application/json' }
      : undefined;

    const bodyPayload = body ? { body: JSON.stringify(body) } : undefined;

    const response = await this.fetchApiBackstage.fetch(
      `${baseUrl}/${apiPath}`,
      {
        method,
        headers: {
          Accept: 'application/json',
          ...contentType,
        },
        ...bodyPayload,
      },
    );
    if (!response.ok) {
      throw await BackstageResponseError.fromResponse(response);
    }

    return response;
  }

  async listClusters(): Promise<Cluster[]> {
    const response = await this.fetch('clusters', 'GET');
    return response.json();
  }

  async listDatabases(): Promise<Database[]> {
    const response = await this.fetch('databases', 'GET');
    return response.json();
  }

  async getLatestDatabaseVersion(): Promise<string> {
    const response = await this.fetch('latest-database-version', 'GET');
    return response.json();
  }

  async getLatestOperatorVersion(): Promise<string> {
    const response = await this.fetch('latest-operator-version', 'GET');
    return response.json();
  }

  async getClusterOrchestra(clusterId: string): Promise<string> {
    const response = await this.fetch(`clusters/${clusterId}/orchestra`, 'GET');
    return response.json();
  }

  async getClusterDatabases(
    clusterId: string,
  ): Promise<ClusterDatabaseEntry[]> {
    const response = await this.fetch(
      `clusters/${encodeURIComponent(clusterId)}/databases`,
      'GET',
    );

    return response.json();
  }

  async getOrchestraGcpProjectId(
    orchestraName: string,
  ): Promise<string | null> {
    const response = await this.fetch(
      `orchestras/${encodeURIComponent(orchestraName)}/gcp-project-id`,
      'GET',
    );

    return response.json();
  }

  async getDatabase(databaseId: string): Promise<TypesDatabase> {
    try {
      const databasesApi = await this.getDatabasesApi();
      return await databasesApi.dbidDetailsGet({ dbid: databaseId });
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async getDatabaseTombstone(
    databaseId: string,
  ): Promise<TypesDatabaseMetadata> {
    try {
      const databasesApi = await this.getDatabasesApi();
      return await databasesApi.dbidTombstoneGet({ dbid: databaseId });
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async getServerDatabases(
    serverName: string,
    orchestra: string,
  ): Promise<ApiServerDatabases> {
    try {
      const serversApi = await this.getServersApi();
      return await serversApi.serversServerNameDatabasesGet({
        serverName,
        orchestra,
      });
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async getCluster(
    clusterId: string,
    orchestra?: string,
  ): Promise<V1Neo4jCluster> {
    try {
      const clustersApi = await this.getClustersApi();
      return await clustersApi.clustersClusteridGet({
        clusterid: clusterId,
        orchestra,
      });
    } catch (error) {
      return this.handleSdkError(error);
    }
  }
}

export const databaseManagerApiRef: ApiRef<DatabaseManagerRestApi> =
  createApiRef<DatabaseManagerRestApi>({
    id: 'plugin.database-manager.service',
  });
